import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, DeviceMobile, EnvelopeSimple } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";

export default function MfaVerifyPage() {
  const { verifyMfa, pendingChallenge } = useAuth();
  const navigate = useNavigate();
  const [method, setMethod] = useState("totp");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (!pendingChallenge) return <Navigate to="/login" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await verifyMfa(code, method);
      toast.success("Identity verified");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 bg-grid px-4">
      <div className="w-full max-w-lg bg-white border border-zinc-950">
        <div className="grid grid-cols-[80px_1fr] border-b border-zinc-950">
          <div className="border-r border-zinc-950 flex items-center justify-center bg-zinc-950 text-white">
            <ShieldCheck size={28} weight="fill" />
          </div>
          <div className="p-5">
            <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">/ MFA_CHALLENGE</div>
            <div className="font-display text-2xl font-black tracking-tight">Verify your identity.</div>
            <div className="text-xs text-zinc-500 mt-1">
              An email code was sent to <span className="font-mono text-zinc-950">{pendingChallenge.email_hint}</span>.
            </div>
          </div>
        </div>

        {/* Method tabs */}
        <div className="grid grid-cols-2 border-b border-zinc-950">
          <button
            data-testid="mfa-tab-totp"
            onClick={() => setMethod("totp")}
            className={`flex items-center justify-center gap-2 py-3 text-xs font-semibold tracking-[0.1em] uppercase border-r border-zinc-950 transition-colors ${
              method === "totp" ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            <DeviceMobile size={14} /> Authenticator
          </button>
          <button
            data-testid="mfa-tab-email"
            onClick={() => setMethod("email")}
            className={`flex items-center justify-center gap-2 py-3 text-xs font-semibold tracking-[0.1em] uppercase transition-colors ${
              method === "email" ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            <EnvelopeSimple size={14} /> Email OTP
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-5" data-testid="mfa-form">
          <div>
            <label className="label-sm text-zinc-500">Enter 6-digit code</label>
            <input
              data-testid="mfa-code-input"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              required
              autoFocus
              className="w-full border border-zinc-300 focus:border-zinc-950 px-4 py-3 text-center tnum text-3xl tracking-[0.5em] bg-white outline-none transition-colors"
              placeholder="••••••"
            />
          </div>

          {error && (
            <div className="border border-[#D0021B] bg-red-50 text-[#D0021B] text-xs px-3 py-2 font-mono" data-testid="mfa-error">
              {error}
            </div>
          )}

          <button
            data-testid="mfa-submit-btn"
            type="submit"
            disabled={loading || code.length < 6}
            className="w-full bg-zinc-950 text-white font-semibold text-sm py-3 hover:bg-[#FF3E00] disabled:opacity-50 transition-colors"
          >
            {loading ? "VERIFYING…" : "VERIFY & SIGN IN"}
          </button>

          <div className="font-mono text-[10px] text-zinc-400 leading-relaxed">
            NOTE / Email OTP is delivered to backend logs in this preview build (MOCKED).
            Check <code>/var/log/supervisor/backend.*.log</code> or the <code>email_log</code> collection for the code.
          </div>
        </form>
      </div>
    </div>
  );
}
